"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const key_1 = require("../lib/key");
class Tabs extends HTMLElement {
    constructor() {
        super();
        this.current = null;
        this.tabs = [];
        this.panels = [];
        this.update = this.update.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
    }
    connectedCallback() {
        if (!this.id) {
            throw new Error('The tabs component requires an id attribute');
        }
        this.tabs = Array.from(this.querySelectorAll('a[href^="#"]'));
        this.panels = this.tabs.map((tab) => {
            const id = tab.hash.slice(1);
            const panel = document.getElementById(id);
            if (panel === null) {
                throw new Error(`Invalid tab panel id "${id}"`);
            }
            return panel;
        });
        if (this.dataset.mediaQuery) {
            const media = window.matchMedia(this.dataset.mediaQuery);
            media.addListener(this.update);
            this.update(media);
        }
        else {
            this.init();
        }
        if (this.current !== null && window.location.hash) {
            const index = this.tabs.findIndex((tab) => {
                return tab.hash === window.location.hash;
            });
            if (index !== -1) {
                this.change(index, false);
            }
        }
    }
    update({ matches }) {
        matches ? this.init() : this.destroy();
    }
    init() {
        this.setAttribute('role', 'tablist');
        this.tabs.forEach((tab, index) => {
            tab.id = `${this.id}-tab-${index}`;
            tab.tabIndex = -1;
            tab.setAttribute('role', 'tab');
            tab.addEventListener('click', this.handleClick);
            tab.addEventListener('keydown', this.handleKeydown);
        });
        this.panels.forEach((panel, index) => {
            panel.tabIndex = -1;
            panel.hidden = true;
            panel.setAttribute('role', 'tabpanel');
            panel.setAttribute('aria-labelledby', this.tabs[index].id);
        });
        this.change(0, false);
    }
    destroy() {
        this.current = null;
        this.removeAttribute('role');
        this.tabs.forEach((tab) => {
            tab.tabIndex = 0;
            tab.removeAttribute('role');
            tab.removeAttribute('aria-selected');
            tab.removeEventListener('click', this.handleClick);
            tab.removeEventListener('keydown', this.handleKeydown);
        });
        this.panels.forEach((panel) => {
            panel.hidden = false;
            panel.removeAttribute('tabindex');
            panel.removeAttribute('role');
            panel.removeAttribute('aria-labelledby');
        });
    }
    handleClick(event) {
        this.change(this.getClickedTabIndex(event));
        event.preventDefault();
    }
    handleKeydown(event) {
        const index = this.getClickedTabIndex(event);
        if (event.key === key_1.ArrowKey.DOWN) {
            this.panels[index].focus();
            event.preventDefault();
        }
        else if (event.key === key_1.ArrowKey.LEFT) {
            this.change(index - 1);
            event.preventDefault();
        }
        else if (event.key === key_1.ArrowKey.RIGHT) {
            this.change(index + 1);
            event.preventDefault();
        }
    }
    change(index, focus = true) {
        if (this.tabs[index] === undefined) {
            return;
        }
        if (this.current !== null) {
            this.hide(this.current);
        }
        this.show(index, focus);
    }
    show(index, focus) {
        const tab = this.tabs[index];
        const panel = this.panels[index];
        tab.tabIndex = 0;
        tab.setAttribute('aria-selected', 'true');
        if (focus) {
            tab.focus();
        }
        panel.hidden = false;
        this.current = index;
    }
    hide(index) {
        const tab = this.tabs[index];
        const panel = this.panels[index];
        tab.tabIndex = -1;
        tab.removeAttribute('aria-selected');
        panel.hidden = true;
    }
    getClickedTabIndex({ target }) {
        if (target instanceof HTMLAnchorElement) {
            return this.tabs.indexOf(target);
        }
        throw new Error();
    }
}
exports.default = Tabs;
customElements.define('sarsons-tabs', Tabs, {
    extends: 'nav',
});
